import { createIsomorphicFn } from "@tanstack/react-start";
import { getRequestRuntime } from "~/lib/ssr/request-runtime.server";
import { getVmPreference } from "~/lib/auth/vm-preference";
import { subscribeAppAuthTransportSnapshot } from "~/lib/auth/app-auth-runtime";
import {
  createBrowserPlatformApiTrpcAdapter,
  type PlatformApiTrpcClient,
} from "./platform-api-adapter";

let singleton: PlatformApiTrpcClient | null = null;
let unbatchedSingleton: PlatformApiTrpcClient | null = null;
let accountingSingleton: PlatformApiTrpcClient | null = null;
let preloadSingleton: PlatformApiTrpcClient | null = null;
let subscriptionSingleton: PlatformApiTrpcClient | null = null;
let authRuntimeSubscriptionInitialized = false;

function ensureAuthRuntimeSubscription(): void {
  if (authRuntimeSubscriptionInitialized) {
    return;
  }

  authRuntimeSubscriptionInitialized = true;
  subscribeAppAuthTransportSnapshot(() => {
    resetBrowserPlatformApiClient();
  });
}

function getBrowserPlatformApiClientInBrowser(): PlatformApiTrpcClient {
  ensureAuthRuntimeSubscription();
  if (!singleton) {
    singleton = createBrowserPlatformApiTrpcAdapter({
      batch: true,
      getVmPreference: () => getVmPreference(),
    });
  }

  return singleton;
}

function getBrowserPlatformApiUnbatchedClientInBrowser(): PlatformApiTrpcClient {
  ensureAuthRuntimeSubscription();
  if (!unbatchedSingleton) {
    unbatchedSingleton = createBrowserPlatformApiTrpcAdapter({
      batch: false,
      getVmPreference: () => getVmPreference(),
    });
  }

  return unbatchedSingleton;
}

/**
 * The `v3.portfolio.*` accounting reads walk a portfolio's full accounting
 * history and can outlast the default request budget on a heavy portfolio.
 * They run unbatched, so a slow one never aborts the page's other reads, and
 * with their own timeout; the page shows placeholders until they land.
 */
export const PORTFOLIO_ACCOUNTING_REQUEST_TIMEOUT_MS = 90_000;

function getBrowserPlatformApiAccountingClientInBrowser(): PlatformApiTrpcClient {
  ensureAuthRuntimeSubscription();
  if (!accountingSingleton) {
    accountingSingleton = createBrowserPlatformApiTrpcAdapter({
      batch: false,
      getVmPreference: () => getVmPreference(),
      requestTimeoutMs: PORTFOLIO_ACCOUNTING_REQUEST_TIMEOUT_MS,
    });
  }

  return accountingSingleton;
}

function getBrowserPlatformApiPreloadClientInBrowser(): PlatformApiTrpcClient {
  ensureAuthRuntimeSubscription();
  if (!preloadSingleton) {
    preloadSingleton = createBrowserPlatformApiTrpcAdapter({
      batch: true,
      getVmPreference: () => getVmPreference(),
    });
  }

  return preloadSingleton;
}

function getBrowserPlatformApiSubscriptionClientInBrowser(): PlatformApiTrpcClient {
  ensureAuthRuntimeSubscription();
  if (!subscriptionSingleton) {
    subscriptionSingleton = createBrowserPlatformApiTrpcAdapter({
      enableSubscriptions: true,
      getVmPreference: () => getVmPreference(),
    });
  }

  return subscriptionSingleton;
}

export function resetBrowserPlatformApiClient() {
  singleton = null;
  unbatchedSingleton = null;
  accountingSingleton = null;
  preloadSingleton = null;
  subscriptionSingleton = null;
}

export const getBrowserPlatformApiClient = createIsomorphicFn()
  .server(() => getRequestRuntime().platformApi)
  .client(getBrowserPlatformApiClientInBrowser);

export const getBrowserPlatformApiUnbatchedClient = createIsomorphicFn()
  .server(() => getRequestRuntime().platformApi)
  .client(getBrowserPlatformApiUnbatchedClientInBrowser);

export const getBrowserPlatformApiAccountingClient = createIsomorphicFn()
  .server(() => getRequestRuntime().platformApi)
  .client(getBrowserPlatformApiAccountingClientInBrowser);

export const getBrowserPlatformApiPreloadClient = createIsomorphicFn()
  .server(() => getRequestRuntime().platformApi)
  .client(getBrowserPlatformApiPreloadClientInBrowser);

export const getBrowserPlatformApiSubscriptionClient = createIsomorphicFn()
  .server(() => getRequestRuntime().platformApi)
  .client(getBrowserPlatformApiSubscriptionClientInBrowser);
